import React, { useState, useEffect, useContext } from "react";
import { Form } from "react-bootstrap";
import moment from "moment";
import Swal from "sweetalert2";
import { FUND_AMOUNT_ERROR, MINIMUM_AMOUNT_ERROR, SELECT_DATE_ERROR, TEXT_LENGTH, VALID_FORMAT_ERROR } from "../../utils/helpers/globalErrorMessage";
import { postApi, getApi } from "../../utils/services";
import { PETTY_CASH_FUND_ADD, PETTY_CASH_FUND_LATEST } from "../../utils/apiRoutes";
import { ModalContext } from "../../utils/Context";

const AddFundModal = ({ show, onHide, fetchFundsList, fundsList }) => {
    const user = JSON.parse(localStorage.getItem("userData"));
    const [formData, setFormData] = useState({
        fundAmount: "",
        date: moment().format("YYYY-MM-DD"),
        description: ""
    });
    const [errors, setErrors] = useState({
        fundAmount: "",
        date: "",
        description: ""
    });
    const [fundNumber, setFundNumber] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const modalContext = useContext(ModalContext);
    const { handleModalData } = modalContext;
    const { closeModal } = modalContext;

    useEffect(() => {
        const fetchLatestFundNumber = async () => {
            if (formData.date) {
                const selectedDate = moment(formData.date);
                const startDate = selectedDate.startOf('month').format('YYYY-MM-DD');
                const endDate = selectedDate.endOf('month').format('YYYY-MM-DD');

                try {
                    const response = await getApi(`${PETTY_CASH_FUND_LATEST}?startDate=${startDate}&endDate=${endDate}`);
                    generateFundNumber(formData.date, response.res.latestFundNo);
                } catch (error) {
                    console.error("Error fetching latest fund number:", error);
                    if (fundsList.length > 0) {
                        generateFundNumber(formData.date);
                    }
                }
            }
        };

        fetchLatestFundNumber();
    }, [formData.date, fundsList]);

    const generateFundNumber = (selectedDate, latestFundNo = null) => {
        const date = moment(selectedDate);
        const month = date.format('MMM').toUpperCase();
        const year = date.format('YYYY');
        const prefix = `${month}${year}_`;

        let newSeq = "001";

        if (latestFundNo) {
            const parts = latestFundNo.split('_');
            if (parts.length > 1) {
                const seq = parseInt(parts[1]);
                newSeq = String(seq + 1).padStart(3, '0');
            }
        } else if (fundsList.length > 0) {
            const matchingFunds = fundsList.filter(fund =>
                fund.FundNo && fund.FundNo.startsWith(prefix)
            );

            if (matchingFunds.length > 0) {
                let maxSeq = 0;
                matchingFunds.forEach(fund => {
                    const parts = fund.FundNo.split('_');
                    if (parts.length > 1) {
                        const seq = parseInt(parts[1]);
                        if (seq > maxSeq) maxSeq = seq;
                    }
                });
                newSeq = String(maxSeq + 1).padStart(3, '0');
            }
        }

        setFundNumber(`${prefix}${newSeq}`);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "description" && value.length > 200) {
            setErrors(prev => ({
                ...prev,
                [name]: TEXT_LENGTH
            }));
            return;
        }

        setErrors(prev => ({
            ...prev,
            [name]: ""
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAmountChange = (e) => {
        const { name, value } = e.target;
        const regex = /^\d*$/;

        if (regex.test(value)) {
            setErrors(prev => ({
                ...prev,
                [name]: ""
            }));
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDateChange = (e) => {
        const selectedDate = e.target.value;
        setErrors(prev => ({
            ...prev,
            date: ""
        }));
        setFormData(prev => ({
            ...prev,
            date: selectedDate
        }));
    };

    const isValidDateFormat = (dateStr) => {
        const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
        return regex.test(dateStr);
    };

    const validateForm = () => {
        let valid = true;
        const newErrors = {
            fundAmount: "",
            date: "",
            description: ""
        };

        if (!formData.fundAmount || isNaN(formData.fundAmount)) {
            newErrors.fundAmount = FUND_AMOUNT_ERROR;
            valid = false;
        } else if (Number(formData.fundAmount) === 0) {
            newErrors.fundAmount = MINIMUM_AMOUNT_ERROR;
            valid = false;
        }

        if (!formData.date) {
            newErrors.date = SELECT_DATE_ERROR;
            valid = false;
        } else if (!isValidDateFormat(formData.date)) {
            newErrors.date = VALID_FORMAT_ERROR;
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setSubmitting(true);
            const payload = {
                fundAmount: parseFloat(formData.fundAmount),
                date: formData.date,
                description: formData.description,
                userId: user.id,
                username: user.Name,
                fundNo: fundNumber
            };

            const result = await postApi(PETTY_CASH_FUND_ADD, payload);

            if (result.res.success) {
                Swal.fire({
                    position: "center",
                    icon: "success",
                    title: result.res.message,
                    showConfirmButton: false,
                    timer: 2000,
                });

                setFormData({
                    fundAmount: "",
                    date: moment().format("YYYY-MM-DD"),
                    description: ""
                });

                fetchFundsList();

                closeModal();
            }

        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="cstm-frm-mdl">
            <div className="modal-header d-flex align-item-center justify-content-between">
                <h4 className="modal-title">Add Fund</h4>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="modal-body">
                    <div className="row">
                        <div className="col-md-6 mb-20 position-relative">
                            <label htmlFor="date" className="form-label">
                                Date<span className="required">*</span>
                            </label>
                            <input
                                type="date"
                                id="date"
                                className="form-control"
                                name="date"
                                value={formData.date}
                                onChange={handleDateChange}
                            />
                            {errors.date && <span className="error-text">{errors.date}</span>}
                        </div>

                        <div className="col-md-6 mb-20 position-relative">
                            <label htmlFor="amount" className="form-label">
                                Fund Amount<span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="amount"
                                className="form-control"
                                name="fundAmount"
                                value={formData.fundAmount}
                                onChange={handleAmountChange}
                                placeholder="Amount"
                            />
                            {errors.fundAmount && <span className="error-text">{errors.fundAmount}</span>}
                        </div>

                        <div className="col-md-12 mb-20 position-relative">
                            <label htmlFor="description" className="form-label">
                                Description
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="1"
                                onChange={handleChange}
                                value={formData.description}
                                className="form-control"
                            />
                            {errors.description && <span className="error-text">{errors.description}</span>}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="d-flex align-items-center mb-20">
                        <div className="danger-outline-btn">
                            <button
                                type="button"
                                className="btn"
                                onClick={() => closeModal()}
                            >
                                Cancel
                            </button>
                        </div>
                        <div className="success-btn ms-2">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="btn"
                            >
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Loading...
                                    </>
                                ) : (
                                    "Add"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default AddFundModal;